import { ChevronDownIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';

export type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectFilterProps = {
  id: string;
  label: string;
  allLabel: string;
  options: readonly MultiSelectOption[];
  values: readonly string[] | undefined;
  onChange: (values: string[] | undefined) => void;
};

function summarize(
  values: readonly string[],
  options: readonly MultiSelectOption[],
  allLabel: string,
) {
  if (values.length === 0) {
    return allLabel;
  }
  if (values.length === 1) {
    return options.find((option) => option.value === values[0])?.label ?? '1 ausgewählt';
  }
  return `${values.length} ausgewählt`;
}

export function MultiSelectFilter({
  id,
  label,
  allLabel,
  options,
  values,
  onChange,
}: MultiSelectFilterProps) {
  const selected = values ?? [];

  function toggle(value: string, checked: boolean) {
    const next = checked
      ? [...new Set([...selected, value])]
      : selected.filter((entry) => entry !== value);
    onChange(next.length > 0 ? next : undefined);
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className="w-full min-w-0 justify-between font-normal"
          >
            <span className="min-w-0 truncate">{summarize(selected, options, allLabel)}</span>
            <ChevronDownIcon
              className="size-4 shrink-0 text-muted-foreground opacity-60"
              aria-hidden
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-72 w-[var(--radix-dropdown-menu-trigger-width)]"
        >
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={selected.length === 0}
            onCheckedChange={(checked) => {
              if (checked) {
                onChange(undefined);
              }
            }}
            onSelect={(event) => event.preventDefault()}
          >
            {allLabel}
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={selected.includes(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked === true)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
